import React, { useState } from 'react';
import {
  Box, Button, Collapse, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Factory, Plus, Cpu, PackageCheck, AlertTriangle, GripVertical } from 'lucide-react';
import { PageHeader, KpiCard, StatusChip, Panel, FormDialog, LoadGate, PageShell } from './businessUi';
import { computeAllMovements, computeStockLevels, computeMaterialPlan, formatNumber } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';
import { StatusTimeline, withStatusChange, todayStr } from './statusHistory';

const KEYS = { production: ERP_KEYS.production, boms: ERP_KEYS.boms, inventory: ERP_KEYS.inventory, manualMoves: ERP_KEYS.movements };
const COLUMNS = [
  { key: 'Planned', accent: 'linear-gradient(90deg,#0ea5e9,#38bdf8)', dot: '#0ea5e9' },
  { key: 'In Progress', accent: 'linear-gradient(90deg,#f59e0b,#fbbf24)', dot: '#f59e0b' },
  { key: 'Completed', accent: 'linear-gradient(90deg,#10b981,#34d399)', dot: '#10b981' },
];
const prodTone = { Planned: 'sky', 'In Progress': 'warning', Completed: 'success' };

// Only forward moves are allowed: Planned → In Progress → Completed.
const NEXT_STATUS = { Planned: 'In Progress', 'In Progress': 'Completed' };

export default function Production() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const productionOrders = data?.production || [];
  const boms = data?.boms || [];
  const inventory = data?.inventory || [];
  const manualMoves = data?.manualMoves || [];

  const addFields = [
    { name: 'fg', label: 'Finished Good (BOM)', type: 'select', required: true, options: boms.length ? boms.map((b) => ({ value: b.fg, label: `${b.fg} — ${b.name}` })) : [{ value: '', label: 'No BOMs defined yet' }] },
    { name: 'qty', label: 'Quantity', type: 'number', default: 100 },
    { name: 'machine', label: 'Machine', required: true, helper: 'e.g. CNC-04' },
    { name: 'date', label: 'Order Date', type: 'date', default: '2026-06-13' },
    { name: 'status', label: 'Status', type: 'select', default: 'Planned', options: ['Planned', 'In Progress', 'Completed'] },
  ];

  const handleAdd = (v) => {
    const next = [{ id: `MO-${9000 + productionOrders.length + 1}`, ...v }, ...productionOrders];
    save('production', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  // Drop: move the dragged card forward one status (Planned→In Progress→Completed only).
  const handleDrop = (targetStatus) => {
    if (!dragId) return;
    const order = productionOrders.find((o) => o.id === dragId);
    if (!order || NEXT_STATUS[order.status] !== targetStatus) return;
    const next = productionOrders.map((o) =>
      o.id === dragId
        ? withStatusChange(o, { status: targetStatus, date: todayStr(), note: 'Moved via kanban' }, 'status')
        : o
    );
    save('production', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
    setDragId(null);
    setDragOver(null);
  };

  const inProgress = productionOrders.filter((o) => o.status === 'In Progress').length;
  const planned = productionOrders.filter((o) => o.status === 'Planned').length;
  const outputQty = productionOrders.filter((o) => o.status === 'Completed').reduce((s, o) => s + o.qty, 0);

  const levels = computeStockLevels(inventory, computeAllMovements(manualMoves, productionOrders, boms));
  const nextOrder = productionOrders.find((o) => o.status === 'Planned') || productionOrders[0];
  const plan = computeMaterialPlan(nextOrder, boms, inventory, levels);
  const shortItems = plan.filter((p) => p.shortfall > 0).length;
  const bom = nextOrder ? boms.find((b) => b.fg === nextOrder.fg) : null;

  return (
    <PageShell>
      <PageHeader
        title="Production Orders"
        subtitle="Drag a card to the next column to advance its status — Planned → In Progress → Completed"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>New Order</Button>}
      />

      <FormDialog open={dialogOpen} title="New Production Order" fields={addFields} onClose={() => setDialogOpen(false)} onSubmit={handleAdd} submitLabel="Create Order" />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, mb: 2.5 }}>
          <KpiCard label="Total Orders" value={String(productionOrders.length)} icon={Factory} tone="primary" />
          <KpiCard label="In Progress" value={String(inProgress)} icon={Cpu} tone="warning" />
          <KpiCard label="Planned" value={String(planned)} icon={Factory} tone="sky" />
          <KpiCard label="Units Produced" value={formatNumber(outputQty)} icon={PackageCheck} tone="success" hint="completed" />
        </Box>

        {boms.length === 0 && (
          <Panel title="No BOMs defined" accent="linear-gradient(90deg,#f59e0b,#fbbf24)" sx={{ mb: 2.5 }}>
            <Typography sx={{ px: 2, py: 2, fontSize: 13, color: 'text.disabled' }}>
              Production orders need a Bill of Materials (finished good → components). No BOMs are stored in ThingsBoard yet, so material planning can't compute requirements. You can still create orders, but the material plan will be empty until a BOM exists.
            </Typography>
          </Panel>
        )}

        {productionOrders.length === 0 ? (
          <Panel title="Work Orders" accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No production orders yet. Click <strong>New Order</strong> to create one — it's saved to ThingsBoard.
            </Typography>
          </Panel>
        ) : (
          <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, mb: 2.5 }}>
            {COLUMNS.map((col) => {
              const orders = productionOrders.filter((o) => o.status === col.key);
              const isDropTarget = dragOver === col.key;
              return (
                <Panel key={col.key} title={col.key} subtitle={`${orders.length} order(s)`} accent={col.accent}>
                  <Box
                    sx={{
                      px: 1.5, pb: 1.5,
                      display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 80,
                      maxHeight: 420, overflowY: 'auto',
                      borderRadius: 2,
                      bgcolor: isDropTarget ? 'rgba(124,92,255,0.06)' : 'transparent',
                      outline: isDropTarget ? '2px dashed #7c5cff' : '2px dashed transparent',
                      transition: 'background-color 0.15s, outline 0.15s',
                    }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
                    onDrop={(e) => { e.preventDefault(); handleDrop(col.key); }}
                  >
                    {orders.length === 0 && (
                      <Typography sx={{ fontSize: 12.5, color: 'text.disabled', textAlign: 'center', py: 2 }}>
                        {isDropTarget ? 'Drop here' : 'No orders'}
                      </Typography>
                    )}
                    {orders.map((o) => {
                      const fg = boms.find((b) => b.fg === o.fg);
                      const isOpen = expandedId === o.id;
                      const canDrag = !!NEXT_STATUS[o.status];
                      const isDragging = dragId === o.id;
                      return (
                        <Box
                          key={o.id}
                          draggable={canDrag}
                          onDragStart={() => setDragId(o.id)}
                          onDragEnd={() => { setDragId(null); setDragOver(null); }}
                          sx={{
                            p: 1.5, borderRadius: 2,
                            border: isDragging ? '1.5px dashed #7c5cff' : '1px solid #eef2f7',
                            bgcolor: isDragging ? 'rgba(124,92,255,0.04)' : '#fafbfc',
                            opacity: isDragging ? 0.55 : 1,
                            cursor: canDrag ? 'grab' : 'default',
                            transition: 'opacity 0.15s, border-color 0.15s',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {canDrag && <GripVertical size={13} color="#94a3b8" />}
                              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.primary' }}>{o.id}</Typography>
                            </Box>
                            <StatusChip label={o.machine} tone="secondary" />
                          </Box>
                          <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>{fg ? fg.name : o.fg}</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
                            <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{o.date}</Typography>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: col.dot }}>{formatNumber(o.qty)} nos</Typography>
                          </Box>
                          <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px dashed', borderTopColor: 'divider' }}>
                            <Button size="small" onClick={() => setExpandedId(isOpen ? null : o.id)} sx={{ textTransform: 'none', fontSize: 11, color: 'text.secondary', minWidth: 0, p: 0.25 }}>
                              {isOpen ? 'Hide history' : 'History'}
                            </Button>
                          </Box>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <StatusTimeline record={o} statusField="status" toneFor={(s) => prodTone[s]} />
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                </Panel>
              );
            })}
          </Box>
        )}

        {nextOrder && bom && (
          <Panel
            title={`Material Plan — ${nextOrder.id}`}
            subtitle={`BOM for ${bom.name} × ${formatNumber(nextOrder.qty)} ${bom.outputUom || 'nos'} · ${shortItems ? `${shortItems} item(s) short — raise a PO` : 'all materials available'}`}
            accent={shortItems ? 'linear-gradient(90deg,#f43f5e,#fb7185)' : 'linear-gradient(90deg,#10b981,#34d399)'}
            actions={<AlertTriangle size={18} color={shortItems ? '#f59e0b' : '#10b981'} />}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Required</TableCell>
                    <TableCell align="right">Available</TableCell>
                    <TableCell align="right">Shortfall</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.map((p) => (
                    <TableRow key={p.code} hover>
                      <TableCell>
                        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.code}</Typography>
                        <Typography component="span" sx={{ fontSize: 13, ml: 1 }}>{p.name}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNumber(p.required, p.required % 1 ? 2 : 0)} {p.uom}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatNumber(p.available, p.available % 1 ? 2 : 0)} {p.uom}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: p.shortfall > 0 ? '#be123c' : 'inherit' }}>
                        {p.shortfall > 0 ? `${formatNumber(p.shortfall, p.shortfall % 1 ? 2 : 0)} ${p.uom}` : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <StatusChip label={p.shortfall > 0 ? 'Short' : 'OK'} tone={p.shortfall > 0 ? 'destructive' : 'success'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>
        )}
      </LoadGate>
    </PageShell>
  );
}
